/**
 * The repository the one-click buttons act on — the header's Play and the Audit screen — shared
 * between them and remembered, so it is chosen once and not at every click.
 *
 * Until the person chooses, it is a linked repository where their own agent is listening, or the
 * first linked one.
 */

import { useCallback, useSyncExternalStore } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from './api'
import type { RepositoryLink } from './types'
import { WINDOW_REPOSITORY_KEY, isMainWindow } from './appWindows'

const KEY = 'devpulse.activeRepository'
const listeners = new Set<() => void>()

/** This window's project first (several windows, lib/appWindows.ts), then the one remembered for the app. */
function read(): string | null {
  try {
    return sessionStorage.getItem(WINDOW_REPOSITORY_KEY) ?? localStorage.getItem(KEY)
  } catch {
    return null
  }
}

/** Among linked repositories: the chosen one, else one with an agent listening, else the first. */
export function pickRepository(linked: RepositoryLink[], chosen: string | null): RepositoryLink | null {
  // A project of the person's own before the free one (docs/refactor/49), which is there for when there is none.
  return linked.find((r) => r.id === chosen) ?? linked.find((r) => r.agentListening) ?? linked.find((r) => !r.isDefault) ?? linked[0] ?? null
}

export function useActiveRepository() {
  const chosen = useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    read,
  )
  const { data } = useQuery({ queryKey: ['repositories'], queryFn: api.repositories.list, staleTime: 30_000 })
  const linked = (data ?? []).filter((r) => r.linked)

  const choose = useCallback((id: string) => {
    try {
      // Each window keeps its own project; the main window's is also remembered for the next start.
      sessionStorage.setItem(WINDOW_REPOSITORY_KEY, id)
      if (isMainWindow()) localStorage.setItem(KEY, id)
    } catch {
      // Private storage: the choice lasts until the page closes.
    }
    for (const listener of listeners) listener()
  }, [])

  return { linked, active: pickRepository(linked, chosen), choose, loaded: data !== undefined }
}
